import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFileManagerContext } from 'modules/file_manager/context/FileManagerContext';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import GlobalTooltip from 'components/GlobalTooltip';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { globalSearchEvent } from 'services/generalServices';
import axios from 'axios';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';

function SearchFiles() {
  const {
    query,
    setQuery,
    isCaseFileManager,
    isLeadFileManager,
    caseDetails,
    setOverrideFiles,
    handleGetFiles,
    pageLimit,
    setIsContentSearchActive,
    isContentSearchActive,
  } = useFileManagerContext();
  const { checkSubPermission } = useUserDetailsContext();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [showContent, setShowContent] = useState(false);

  // Sync local toggle with global content-search state (so refresh clears the button)
  useEffect(() => {
    setShowContent(Boolean(isContentSearchActive));
  }, [isContentSearchActive]);
  const cancelTokenSource = useRef(null);

  const canShowContentButton = useMemo(
    () => checkSubPermission('full_text_search') && (isCaseFileManager || isLeadFileManager),
    [checkSubPermission, isCaseFileManager, isLeadFileManager]
  );

  function onChangeSearchFiles(value) {
    setQuery(value);
  }

  // When content search is active, fetch results from Global Search as the query changes
  useEffect(() => {
    if (!isContentSearchActive) return;
    if (!query) {
      setOverrideFiles([]);
      return;
    }
    if (cancelTokenSource.current) {
      cancelTokenSource.current.cancel();
    }
    cancelTokenSource.current = axios.CancelToken.source();
    setLoading(true);
    globalSearchEvent(
      query,
      true,
      true,
      true,
      { cancelToken: cancelTokenSource.current.token },
      '',
      1,
      pageLimit || 50,
      '',
      '',
      '',
      '',
      '',
      '',
      caseDetails?.case_id || ''
    )
      .then((res) => {
        const buckets = res?.data?.results?.documents?.buckets ?? res?.data?.documents?.buckets ?? [];
        const mapped = (buckets || []).map((v) => {
          const src = v?.top_hit?.hits?.hits?.[0]?._source || {};
          return {
            ...src,
            display_name: src?.file_name || src?.display_name,
            entry_type: 'file',
            object_id: src?.document_id,
            file_path: src?.object_key,
            case_id: src?.case_id,
            case_name: src?.case_name,
            size: src?.size,
            time: src?.time,
            res_type: 'file_system_entry',
          };
        });
        setOverrideFiles(mapped);
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          addToast(false, toastConstant.toasterType.ERROR, 'Content Search', 'Failed to search content');
        }
      })
      .finally(() => setLoading(false));
  }, [isContentSearchActive, query, pageLimit, caseDetails?.case_id]);

  return (
    <div className="autowidth my-2 me-2 d-flex align-items-center">
      <div className="p-input-icon-left input-shadow w-100 position-relative">
        <i className="icon-search F-size16" />
        <TextSnippetWrapper
          type="text"
          className=" w-100 input-search"
          value={query || ''}
          onChange={(e) => onChangeSearchFiles(e.target.value)}
          placeholder="Search"
          autoComplete="off"
          style={{ paddingRight: canShowContentButton ? '2.5rem' : undefined }}
        />
        {canShowContentButton && (
          <span
            role="button"
            className="d-inline-flex align-items-center"
            style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 2, pointerEvents: 'auto' }}
            onClick={async () => {
              if (!query && !isContentSearchActive) {
                addToast(false, toastConstant.toasterType.INFO, 'Info', 'Enter a search term');
                return;
              }
              try {
                if (cancelTokenSource.current) {
                  cancelTokenSource.current.cancel();
                }
                cancelTokenSource.current = axios.CancelToken.source();
                setLoading(true);
                const nextActive = !isContentSearchActive;
                setShowContent(nextActive);
                setIsContentSearchActive(nextActive);
                if (!nextActive) {
                  // Turning OFF content search: clear overrides and reload files via file API
                  setOverrideFiles(null);
                  try {
                    await Promise.resolve(handleGetFiles(pageLimit));
                  } finally {
                    setLoading(false);
                  }
                  return;
                }
                // If turning ON, effect will fetch based on current query
              } catch (error) {
                if (!axios.isCancel(error)) {
                  addToast(false, toastConstant.toasterType.ERROR, 'Content Search', 'Failed to search content');
                }
              } finally {
                setLoading(false);
              }
            }}
          >
            <GlobalTooltip
              tooltip="Search Content Data"
              className="ms-2"
              style={isContentSearchActive ? { background: 'var(--primary-main)', color: '#fff', borderRadius: 6, padding: '4px' } : {}}
            >
              {loading ? (
                <i className="pi pi-spin pi-spinner fa-lg" />
              ) : (
                <i
                  className={`icon-files fa-lg ${isContentSearchActive ? 'highlight' : ''}`}
                  style={isContentSearchActive ? { color: '#fff' } : {}}
                />
              )}
            </GlobalTooltip>
          </span>
        )}
      </div>
    </div>
  );
}

export default SearchFiles;
